export const CLEIC_LIVE_LEDGER_ORIGIN = "https://ledger.cleic.com";
export const CLEIC_LIVE_LENS_ORIGIN = "https://lens.cleic.com";
/** Stable Preview release-channel hosts — never live-only redirected. */
export const CLEIC_STABLE_PREVIEW_CHANNEL_HOSTS = Object.freeze([
    "preview-ledger.cleic.com",
    "preview-lens.cleic.com",
]);
export const CLEIC_PAUSED_CHANNEL_HOSTS = Object.freeze([
    "lab-ledger.cleic.com",
    "preview-ledger.cleic.com",
    "lab-lens.cleic.com",
    "preview-lens.cleic.com",
    "preview-data.cleic.com",
    "lab-data.cleic.com",
    "preview-auth.cleic.com",
    "lab-auth.cleic.com",
]);
/** Windows/macOS hosts-file dev aliases — mirror prod subdomain shape on loopback. */
export const CLEIC_LOCAL_CHANNEL_HOSTS = Object.freeze([
    "local-ledger.cleic.com",
    "local-lens.cleic.com",
    "local-data.cleic.com",
    "local.cleic.com",
]);
export const CLEIC_LOCAL_LEDGER_ORIGIN = "http://local-ledger.cleic.com:5173";
export const CLEIC_LOCAL_LENS_ORIGIN = "http://local-lens.cleic.com:5174";
export const CLEIC_LOCAL_DATA_ORIGIN = "http://local-data.cleic.com:5178";
/** Direct Hono API (server-to-server); UI uses :5178 Vite proxy to /api. */
export const CLEIC_LOCAL_DATA_API_ORIGIN = "http://127.0.0.1:3003";
const LIVE_ONLY_FALSE = new Set(["0", "false", "off", "no"]);
const LIVE_ONLY_TRUE = new Set(["1", "true", "on", "yes"]);
export function parseCleicLiveOnlyFlag(raw, fallback = false) {
    const normalized = String(raw ?? "").trim().toLowerCase();
    if (!normalized)
        return fallback;
    if (LIVE_ONLY_FALSE.has(normalized))
        return false;
    if (LIVE_ONLY_TRUE.has(normalized))
        return true;
    return fallback;
}
export function isCleicStablePreviewChannelHost(hostname) {
    const host = String(hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (!host)
        return false;
    return CLEIC_STABLE_PREVIEW_CHANNEL_HOSTS.includes(host);
}
/** Whether client/server should enforce live-only channel redirects. */
export function resolveCleicLiveOnlyChannelsFlag(input) {
    const vercelEnv = String(input.vercelEnv ?? "").trim().toLowerCase();
    if (vercelEnv === "preview") {
        return parseCleicLiveOnlyFlag(input.rawLiveOnly, false);
    }
    const prodFallback = typeof input.prod === "boolean"
        ? input.prod
        : String(input.nodeEnv ?? "").trim().toLowerCase() === "production";
    return parseCleicLiveOnlyFlag(input.rawLiveOnly, prodFallback);
}
export function isPausedCleicChannelHost(hostname) {
    const host = String(hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (!host)
        return false;
    return CLEIC_PAUSED_CHANNEL_HOSTS.includes(host);
}
export function isCleicLocalChannelHost(hostname) {
    const host = String(hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (!host)
        return false;
    return CLEIC_LOCAL_CHANNEL_HOSTS.includes(host);
}
export function isCleicPreviewDeploymentHost(hostname) {
    const host = String(hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (!host.endsWith(".vercel.app"))
        return false;
    return (host.includes("cleic-ledger") ||
        host.includes("cleic-lens") ||
        host.includes("cleic-www") ||
        host.includes("cleic-data"));
}
export function resolveCleicLiveOriginForHost(hostname) {
    const host = String(hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (!host)
        return null;
    if (host.includes("lens") || host.startsWith("preview-lens.") || host.startsWith("lab-lens.")) {
        return CLEIC_LIVE_LENS_ORIGIN;
    }
    if (host.includes("ledger") ||
        host.startsWith("preview-ledger.") ||
        host.startsWith("lab-ledger.") ||
        host.includes("cleic-ledger")) {
        return CLEIC_LIVE_LEDGER_ORIGIN;
    }
    if (host.includes("cleic-lens"))
        return CLEIC_LIVE_LENS_ORIGIN;
    if (host.includes("cleic-ledger"))
        return CLEIC_LIVE_LEDGER_ORIGIN;
    return null;
}
export function buildCleicLiveRedirectUrl(input, options) {
    if (!options.liveOnly)
        return null;
    const host = input.hostname.split(":")[0]?.trim().toLowerCase();
    if (isCleicStablePreviewChannelHost(host))
        return null;
    const pausedHost = isPausedCleicChannelHost(host);
    const previewDeployment = isCleicPreviewDeploymentHost(host);
    if (!pausedHost && !previewDeployment)
        return null;
    const liveOrigin = resolveCleicLiveOriginForHost(host);
    if (!liveOrigin)
        return null;
    const target = new URL(liveOrigin);
    const pathname = String(input.pathname ?? "/").trim() || "/";
    target.pathname = pathname;
    target.search = String(input.search ?? "");
    target.hash = String(input.hash ?? "");
    return target.toString();
}
export function normalizeCleicAppChannel(value) {
    const normalized = String(value ?? "").trim().toLowerCase();
    if (normalized === "live" || normalized === "prod" || normalized === "production")
        return "live";
    if (normalized === "preview" || normalized === "pr")
        return "preview";
    if (normalized === "lab" || normalized === "staging" || normalized === "stage")
        return "lab";
    return null;
}
export function inferCleicAppChannelFromHost(hostname) {
    const host = String(hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (isCleicLocalChannelHost(host))
        return "lab";
    if (host === "lab-ledger.cleic.com" || host === "lab-lens.cleic.com")
        return "lab";
    if (host === "preview-ledger.cleic.com" || host === "preview-lens.cleic.com")
        return "preview";
    if (host === "ledger.cleic.com" || host === "lens.cleic.com")
        return "live";
    return null;
}
export function resolveCleicAppChannel(input) {
    const host = String(input.hostname ?? "").split(":")[0]?.trim().toLowerCase();
    if (input.liveOnly && !isCleicStablePreviewChannelHost(host))
        return "live";
    const explicit = normalizeCleicAppChannel(input.explicitChannel);
    if (explicit)
        return explicit;
    return inferCleicAppChannelFromHost(input.hostname) ?? "live";
}
//# sourceMappingURL=channels.js.map